/**
 * Focus ring utilities for interactive controls. Ring styles are suppressed when
 * `data-disable-focus-ring` is present (e.g. sole tabbable inside a container that
 * already renders focus chrome).
 *
 * Use {@link DISABLE_FOCUS_RING_DATA_ATTR} when passing the attribute from React.
 *
 * **Tailwind:** Each `not-disable-focus-ring:…` utility appears below as a **full**
 * substring inside a string literal (see `v4-style.css` `@custom-variant not-disable-focus-ring`).
 * Do not build those names from variables or template prefixes — the content scanner may
 * not emit the CSS.
 */
export const DISABLE_FOCUS_RING_DATA_ATTR = 'data-disable-focus-ring' as const;

/** Spread onto a DS control when an ancestor already renders focus chrome (e.g. resourcing grid cell). */
export function suppressFocusRingProps(
  suppress: boolean
): Record<string, string> | undefined {
  return suppress ? { [DISABLE_FOCUS_RING_DATA_ATTR]: '' } : undefined;
}

const focusWithinChildRing =
  'has-focus-ring outline-none ring-offset-bg-default transition-all not-disable-focus-ring:not-aria-readonly:has-focus-inside-no-own-ring:outline-hidden not-disable-focus-ring:not-aria-readonly:has-focus-inside-no-own-ring:ring-2 not-disable-focus-ring:not-aria-readonly:has-focus-inside-no-own-ring:ring-bg-emphasis/70 not-disable-focus-ring:not-aria-readonly:has-focus-inside-no-own-ring:ring-offset-2';

export const focusRings = {
  focus: {
    normal:
      'has-focus-ring outline-none ring-offset-bg-default transition-all not-disable-focus-ring:focus:outline-hidden not-disable-focus-ring:not-aria-readonly:focus:ring-2 not-disable-focus-ring:not-aria-readonly:focus:ring-bg-emphasis/70 not-disable-focus-ring:not-aria-readonly:focus:ring-offset-2',
    visible:
      'has-focus-ring outline-none ring-offset-bg-default transition-all not-disable-focus-ring:focus-visible:outline-hidden not-disable-focus-ring:not-aria-readonly:focus-visible:ring-2 not-disable-focus-ring:not-aria-readonly:focus-visible:ring-bg-emphasis/70 not-disable-focus-ring:not-aria-readonly:focus-visible:ring-offset-2',
    /** Ring when focused programmatically on an invalid control (e.g. RHF shouldFocusError). */
    invalidFocused:
      'aria-invalid:focus:outline-hidden not-disable-focus-ring:aria-invalid:not-aria-readonly:focus:ring-2 not-disable-focus-ring:aria-invalid:not-aria-readonly:focus:ring-bg-emphasis/70 not-disable-focus-ring:aria-invalid:not-aria-readonly:focus:ring-offset-2',
    /** Ring when keyboard-focused on an invalid control. */
    invalidFocusedVisible:
      'aria-invalid:focus-visible:outline-hidden not-disable-focus-ring:aria-invalid:not-aria-readonly:focus-visible:ring-2 not-disable-focus-ring:aria-invalid:not-aria-readonly:focus-visible:ring-bg-emphasis/70 not-disable-focus-ring:aria-invalid:not-aria-readonly:focus-visible:ring-offset-2'
  },
  focusWithin: {
    normal: focusWithinChildRing,
    visible: focusWithinChildRing,
    /** Ring when a child is focused inside an invalid container (keyboard or programmatic). */
    invalidFocused:
      'not-disable-focus-ring:aria-invalid:not-aria-readonly:has(:focus):not(:has(.has-focus-ring:focus)):outline-hidden not-disable-focus-ring:aria-invalid:not-aria-readonly:has(:focus):not(:has(.has-focus-ring:focus)):ring-2 not-disable-focus-ring:aria-invalid:not-aria-readonly:has(:focus):not(:has(.has-focus-ring:focus)):ring-bg-emphasis/70 not-disable-focus-ring:aria-invalid:not-aria-readonly:has(:focus):not(:has(.has-focus-ring:focus)):ring-offset-2'
  }
};
